// Which conflicts are going on at an instant, and in what order to list them.

import { CT_SUB, CT_UCDP, CT_UMBRELLA, CT_WAR, type Conflicts } from "./data";

/** Is conflict i active at decimal year t? UCDP conflicts only in their active years (≥ 25 deaths). */
export function isActive(c: Conflicts, i: number, t: number): boolean {
  if (!(t >= c.t0[i] && t <= c.t1[i]) || c.bt[i]) return false;
  const runs = c.runs[i];
  if (!runs) return true;
  const y = Math.floor(t);
  for (const [a, b] of runs) if (y >= a && y <= b) return true;
  return false;
}

/** Deaths recorded (UCDP) in the calendar year of t, or null when not measured. */
export function deathsInYear(c: Conflicts, i: number, t: number): number | null {
  const yd = c.yd[i];
  if (!yd) return null;
  const k = Math.floor(t) - yd[0] + 1;
  return k >= 1 && k < yd.length ? yd[k] : null;
}

/** Listing rank: how prominent a conflict is overall, and how intense it was in this year where measured. */
export function rankAt(c: Conflicts, i: number, t: number): number {
  const dy = deathsInYear(c, i, t);
  const intensity = dy == null ? 0 : Math.log10(1 + dy) / 5;
  return c.sig[i] * (dy == null ? 1 : 0.55) + intensity * 0.45 + (c.cur[i] ? (5 - c.cur[i]) * 0.02 : 0);
}

export interface ActiveEntry { i: number; rank: number; deathsYear: number | null }

/** Top-level conflicts active at t (wars and UCDP conflicts; theatres are shown inside their war). */
export function activeRoots(c: Conflicts, t: number, kinds?: boolean[]): ActiveEntry[] {
  const out: ActiveEntry[] = [];
  for (let i = 0; i < c.n; i++) {
    if (c.p[i] !== -1) continue;
    const ct = c.ct[i];
    if (ct !== CT_WAR && ct !== CT_UCDP) continue;
    if (kinds && !kinds[c.kind[i]]) continue;
    if (!isActive(c, i, t)) continue;
    out.push({ i, rank: rankAt(c, i, t), deathsYear: deathsInYear(c, i, t) });
  }
  out.sort((a, b) => b.rank - a.rank);
  return out;
}

/** Children (theatres, UCDP parts) of conflict i active at t. */
export function activeChildren(c: Conflicts, parent: number, t: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < c.n; i++) if (c.p[i] === parent && isActive(c, i, t)) out.push(i);
  return out.sort((a, b) => c.sig[b] - c.sig[a]);
}

/** Conflicts drawn as bars on the timeline for the visible range, most significant first. */
export function timelineCandidates(c: Conflicts, a: number, b: number, limit: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < c.n; i++) {
    if (c.p[i] !== -1 || c.bt[i]) continue;
    const ct = c.ct[i];
    if (ct !== CT_WAR && ct !== CT_UMBRELLA && ct !== CT_UCDP) continue;
    if (c.t1[i] < a || c.t0[i] > b) continue;
    out.push(i);
  }
  out.sort((x, y) => c.sig[y] - c.sig[x]);
  return out.slice(0, limit);
}

export const isSub = (c: Conflicts, i: number) => c.ct[i] === CT_SUB || c.ct[i] === CT_UCDP;
